import {
  type FocusEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  cloneElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export type TooltipAlign = 'start' | 'center' | 'end';

/** How close to the edge of the window a tooltip may come: the page's own side gutter. */
const VIEWPORT_GUTTER_PX = 16;

export interface TooltipProps {
  /** What the tooltip says. It also describes the trigger to a screen reader, open or not. */
  content: ReactNode;
  /**
   * A first line in bold above the content, shown and not read out with it: for a heading the
   * trigger's own name already says, which a screen reader would otherwise hear twice.
   */
  heading?: ReactNode;
  /**
   * Which edge of the trigger the tooltip lines up with. `end` keeps one under a trigger near the
   * right edge of the screen from running off it, and `start` one near the left edge.
   */
  align?: TooltipAlign;
  /**
   * Whether activating the trigger — a click, a tap, Enter or Space — pins the tooltip open, and
   * activating it again puts it away. For a trigger whose only job is the tooltip, where a tap
   * gives no hover and does not focus a button on every touch browser, so it would otherwise
   * flash and close.
   */
  toggleOnClick?: boolean;
  /** The one focusable element the tooltip explains. A description it already has is kept. */
  children: ReactElement<{
    'aria-describedby'?: string;
    onClick?: (event: MouseEvent<HTMLElement>) => void;
  }>;
  className?: string;
  testId?: string;
}

/**
 * A short explanation shown under a control while the pointer is over it or it has focus.
 *
 * Held in state rather than drawn by `:hover` and `:focus` alone, because Escape has to be able to
 * put it away without the pointer or the focus moving — which is what keeps it from covering
 * something the reader wants to see. The tooltip sits inside the element the pointer is tracked
 * on, so moving the pointer from the trigger onto the tooltip to read it keeps it open. Escape
 * another control has already answered — a drawer closing, an edit cancelled — leaves it alone.
 *
 * A tooltip lines up with its trigger and is then moved as little as keeps it inside the window's
 * side gutters, so a narrow window never scrolls sideways to show one.
 */
export const Tooltip = ({
  content,
  heading,
  align = 'center',
  toggleOnClick = false,
  children,
  className,
  testId,
}: TooltipProps) => {
  const id = useId();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  /** Escape put it away; the next hover or focus brings it back. */
  const [dismissed, setDismissed] = useState(false);
  /** Activated open with `toggleOnClick`: it stays open after the pointer and the focus leave. */
  const [pinned, setPinned] = useState(false);
  const open = (hovered || focused || pinned) && !dismissed;
  const bubble = useRef<HTMLSpanElement>(null);
  const wrapper = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const element = bubble.current;
    if (!open || element === null) {
      return;
    }

    element.style.translate = '';
    const { left, right, width } = element.getBoundingClientRect();
    // Nothing is laid out where nothing has a size, so there is nothing to keep on the screen.
    if (width === 0) {
      return;
    }
    const viewport = document.documentElement.clientWidth;
    const shift =
      left < VIEWPORT_GUTTER_PX
        ? VIEWPORT_GUTTER_PX - left
        : right > viewport - VIEWPORT_GUTTER_PX
          ? viewport - VIEWPORT_GUTTER_PX - right
          : 0;
    if (shift !== 0) {
      element.style.translate = `${String(shift)}px 0`;
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    // On the document, so Escape puts away a tooltip the pointer opened while focus is elsewhere.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        setDismissed(true);
        setPinned(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    if (!toggleOnClick || !open) {
      return;
    }

    // A press anywhere else puts the tooltip away, which is how a tap elsewhere ends it. It goes
    // even while the trigger keeps the focus, since a press on something that takes no focus
    // leaves it there; the next hover or focus brings it back.
    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node && wrapper.current?.contains(event.target) === true)) {
        setPinned(false);
        setDismissed(true);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);

    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [toggleOnClick, open]);

  return (
    <span
      className={`tooltip tooltip-${align}${className === undefined ? '' : ` ${className}`}`}
      ref={wrapper}
      onPointerEnter={() => {
        setHovered(true);
        setDismissed(false);
      }}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => {
        setFocused(true);
        setDismissed(false);
      }}
      onBlur={(event: FocusEvent<HTMLSpanElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocused(false);
        }
      }}
    >
      {cloneElement(children, {
        'aria-describedby': [children.props['aria-describedby'], id].filter(Boolean).join(' '),
        ...(toggleOnClick
          ? {
              onClick: (event: MouseEvent<HTMLElement>) => {
                children.props.onClick?.(event);
                // Pinned, it closes even with the pointer or the focus still on the trigger.
                setDismissed(pinned);
                setPinned(!pinned);
              },
            }
          : {}),
      })}
      <span
        className="tooltip-bubble"
        data-test-id={testId}
        hidden={!open}
        ref={bubble}
        role="tooltip"
      >
        {heading !== undefined && <span className="tooltip-title">{heading}</span>}
        <span id={id}>{content}</span>
      </span>
    </span>
  );
};
